import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const AssignMemberModal = ({ open, onClose, player, organizationId, onSave }) => {
  const [mode, setMode] = useState("compare"); // "compare" | "create"
  const [selectedSuggestionId, setSelectedSuggestionId] = useState("");

  const [name, setName] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [cellphoneNumber, setCellphoneNumber] = useState("");
  const [isActive, setIsActive] = useState(true);

  const isUser = player?.playerable_type === "App\\User";
  const targetId = player?.id ?? player?.player_id;
  const suggestions = player?.suggestions || [];
  const selectedSuggestion = suggestions.find((s) => String(s.id) === String(selectedSuggestionId)) || suggestions[0];

  useEffect(() => {
    if (open && player) {
      setName(player.name ?? "");
      setDocumentNumber(player.document_number ?? "");
      setCellphoneNumber(player.cellphone_number ?? "");
      setIsActive(true);

      if (suggestions.length > 0) {
        setMode("compare");
        setSelectedSuggestionId(suggestions[0].id);
      } else {
        setMode("create");
        setSelectedSuggestionId("");
      }
    }
  }, [open, player, suggestions.length]);

  const handleLink = () => {
    if (!selectedSuggestion) return;
    onSave({
      action: "link",
      member_id: selectedSuggestion.id,
      player_id: isUser ? player.player_id : targetId,
      participant_id: player.participant_id,
      ...(isUser ? { user_id: targetId } : {}),
    });
  };

  const handleCreate = () => {
    onSave({
      action: "create",
      name,
      document_number: documentNumber,
      cellphone_number: cellphoneNumber,
      is_active: isActive,
      ...(isUser ? { user_id: targetId } : { player_id: targetId }),
      participant_id: player.participant_id,
    });
  };

  const renderCompareMode = () => (
    <Stack spacing={3} mt={1}>
      <Typography variant="body2" color="text.secondary">
        Revisá la información del jugador inscripto y comparala con el miembro sugerido para confirmar si se trata de la
        misma persona.
      </Typography>

      {suggestions.length > 1 && (
        <FormControl fullWidth size="small">
          <InputLabel>Sugerencias encontradas ({suggestions.length})</InputLabel>
          <Select
            label={`Sugerencias encontradas (${suggestions.length})`}
            value={selectedSuggestionId}
            onChange={(e) => setSelectedSuggestionId(e.target.value)}
          >
            {suggestions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name} {s.document_number ? `(ID: ${s.document_number})` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", bgcolor: "background.paper" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                DATOS DE LA INSCRIPCIÓN
              </Typography>
              <Typography variant="h6" gutterBottom>
                {player?.name}
              </Typography>
              <Stack spacing={1.5} mt={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Documento
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {player?.document_number || "No provisto"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Teléfono
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {player?.cellphone_number || "No provisto"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Cuenta REVA
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {isUser ? "Sí" : "No"}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card
            variant="outlined"
            sx={{ height: "100%", borderColor: "primary.main", borderWidth: 2, bgcolor: "action.hover" }}
          >
            <CardContent>
              <Typography variant="overline" color="primary" gutterBottom display="block">
                MIEMBRO SUGERIDO
              </Typography>
              <Typography variant="h6" gutterBottom>
                {selectedSuggestion?.name}
              </Typography>
              <Stack spacing={1.5} mt={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Documento
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={
                      player?.document_number === selectedSuggestion?.document_number && player?.document_number
                        ? "success.main"
                        : "text.primary"
                    }
                  >
                    {selectedSuggestion?.document_number || "No provisto"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Teléfono
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={
                      player?.cellphone_number === selectedSuggestion?.cellphone_number && player?.cellphone_number
                        ? "success.main"
                        : "text.primary"
                    }
                  >
                    {selectedSuggestion?.cellphone_number || "No provisto"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Motivo de coincidencia
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedSuggestion?.match_reason === "document"
                      ? "Mismo Nro. Documento"
                      : selectedSuggestion?.match_reason === "name"
                        ? "Mismo Nombre"
                        : "Mismo Nro. Teléfono"}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );

  const renderCreateMode = () => (
    <Stack spacing={3} mt={1}>
      <Typography variant="body2" color="text.secondary">
        Modificá o completá los datos para dar de alta a este jugador como un nuevo miembro de tu organización.
      </Typography>
      <TextField label="Nombre y Apellido" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
      <TextField
        label="Número de Documento"
        fullWidth
        value={documentNumber}
        onChange={(e) => setDocumentNumber(e.target.value)}
      />
      <TextField
        label="Teléfono"
        fullWidth
        value={cellphoneNumber}
        onChange={(e) => setCellphoneNumber(e.target.value)}
      />
      <FormControlLabel
        control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
        label="Miembro activo"
      />
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{mode === "compare" ? "Vincular jugador" : "Crear nuevo miembro"}</DialogTitle>
      <DialogContent dividers>{mode === "compare" ? renderCompareMode() : renderCreateMode()}</DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>

        {mode === "compare" ? (
          <Stack direction="row" spacing={2}>
            <Button onClick={() => setMode("create")} color="inherit" variant="outlined">
              No es la misma persona (Crear nuevo)
            </Button>
            <Button onClick={handleLink} variant="contained" color="primary" startIcon={<LinkIcon />}>
              Confirmar vinculación
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={2}>
            {suggestions.length > 0 && (
              <Button onClick={() => setMode("compare")} color="inherit">
                Volver a coincidencias
              </Button>
            )}
            <Button onClick={handleCreate} variant="contained" color="primary" startIcon={<PersonAddIcon />}>
              Crear y vincular miembro
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssignMemberModal;
